import { Button, Col, Flex, Form, Grid, Image, Row } from "antd";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import CalendarDefault from "../../../../assets/images/content/calendar_default.png";
import CalendarSelected from "../../../../assets/images/content/calendar_selected.png";
import SearchDefault from "../../../../assets/images/content/search_default.png";
import SearchSelected from "../../../../assets/images/content/search_selected.png";
import { H6, P3 } from "../../../../components/NormalFonts";
import { useLayout } from "../../../../context/LayoutContext";
import {
  handleClickCS,
  objectToQuerystring,
  pushGtag,
} from "../../../../plugins/utils";
import { steps as resultSteps } from "../../../Result/enumerations/steps";
import { step } from "../../enumerations/step";

import { HomeCalendar, OtherFilter } from "./components";
import FullFilter from "./components/FullFilter";
import { FilterBarProps, FormParams } from "./data";
import styles from "./styles.module.scss";

export type SearchMode = "date" | "resort";

const FilterBar: React.FC<FilterBarProps> = ({}) => {
  const { useBreakpoint } = Grid;
  const screens = useBreakpoint();
  const { getContentPadding } = useLayout();
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const [steps, setSteps] = useState<string>(step.FULL_SEARCH);
  const [otherParams, setOtherParams] = useState<FormParams>();
  const [showHalfDaySelector, setShowHalfDaySelector] = useState(false);
  const [selectedSearchMode, setSelectedSearchMode] =
    useState<SearchMode>("date");

  const handlePrevStep = () => {
    pushGtag({
      event: "home_search_advanced_prev_step",
    });

    setSteps(step.OTHER);
  };

  const stepsEnum: Record<string, any> = {
    [step.FULL_SEARCH]: {
      handleOk: () => {
        const params = form.getFieldsValue();
        delete params.count;
        delete params.date;
        const finalParams = {
          ...params,
          coach_account_id: null,
          coach_designated: 0,
        };
        console.debug("finalParams", finalParams);

        pushGtag({
          event: "home_search_by_date",
          ...finalParams,
        });

        const queryString = objectToQuerystring(finalParams);
        navigate(`/search/?step=${resultSteps.SEARCH}&${queryString}`);
      },
      bodyWidth: "100%",
      showHint: false,
      content: (
        <FullFilter
          form={form}
          params={otherParams}
          setShowHalfDaySelector={setShowHalfDaySelector}
          showHalfDaySelector={showHalfDaySelector}
        />
      ),
      btn: (
        <Col span={screens.lg ? 4 : 24}>
          <Button
            style={{ marginTop: screens.lg ? 24 : 0 }}
            block
            className={styles.submit}
            type="primary"
            htmlType="submit"
          >
            {t("home.searchbar.button.bydate")}
          </Button>
        </Col>
      ),
    },
    [step.OTHER]: {
      handleOk: () => {
        const newOtherParams = form.getFieldsValue();
        delete newOtherParams.count;
        console.debug("newOtherParams", newOtherParams);

        pushGtag({
          event: "home_search_advanced_next_step",
          ...newOtherParams,
        });

        setOtherParams(newOtherParams);
        setSteps(step.DATE);
      },
      bodyWidth: "100%",
      showHint: false,
      content: (
        <OtherFilter
          form={form}
          params={otherParams}
          setShowHalfDaySelector={setShowHalfDaySelector}
          showHalfDaySelector={showHalfDaySelector}
        />
      ),
      btn: (
        <Col span={screens.lg ? (showHalfDaySelector ? 4 : 6) : 24}>
          <Button
            style={{ marginTop: screens.lg ? 24 : 0 }}
            block
            className={styles.submit}
            type="primary"
            htmlType="submit"
          >
            {t("home.searchbar.button")}
          </Button>
        </Col>
      ),
    },
    [step.DATE]: {
      handleOk: () => {
        const dateParams = form.getFieldsValue();
        delete dateParams.date;
        const finalParams = {
          ...otherParams,
          ...dateParams,
          coach_account_id: null,
          coach_designated: 0,
        };
        console.debug("finalParams", finalParams);

        pushGtag({
          event: "home_search_advanced_final",
          ...finalParams,
        });

        const queryString = objectToQuerystring(finalParams);
        navigate(`/search/?step=${resultSteps.SEARCH}&${queryString}`);
      },
      bodyWidth: screens.lg ? "50%" : "100%",
      showHint: true,
      content: <HomeCalendar params={otherParams} form={form} />,
      btn: (
        <Col span={screens.lg ? 12 : 24}>
          <Flex gap={8}>
            <Button
              style={{ marginTop: screens.lg ? 24 : 0, width: "60%" }}
              block
              className={styles.submit}
              type="primary"
              htmlType="submit"
            >
              {t("normal.confirm")}
            </Button>
            <Button
              style={{ marginTop: screens.lg ? 24 : 0, width: "40%" }}
              onClick={handlePrevStep}
            >
              {t("normal.prestep")}
            </Button>
          </Flex>
        </Col>
      ),
    },
  };

  const getBottom = () => {
    if (screens.lg) return "100px";
    if (screens.md) return "30px";
    // if (screens.md) return "108px";
    if (screens.sm) return "108px";
    return "126px"; // Default for xs or smaller
  };

  const getBodyPadding = () => {
    if (screens.lg) return "28px";
    else return "16px 14px";
  };

  useEffect(() => {
    if (!selectedSearchMode) return;
    if (selectedSearchMode === "date") {
      setSteps(step.FULL_SEARCH);
      return;
    }
    setSteps(step.OTHER);
  }, [selectedSearchMode]);

  return (
    <div
      className={styles.filterBarContainer}
      style={{ padding: getContentPadding(), bottom: getBottom() }}
    >
      <div className={styles.headerContainer}>
        <div
          className={`${styles.titleWrapper} ${
            selectedSearchMode === "date" ? styles.selected : ""
          }`}
          onClick={() => setSelectedSearchMode("date")}
        >
          <Image
            className={styles.imageTitle}
            src={
              selectedSearchMode === "date" ? CalendarSelected : CalendarDefault
            }
            preview={false}
          />
          <H6 className={styles.title} weight={"medium"}>
            {t("home.searchbar.bydate")}
          </H6>
        </div>
        <div
          className={`${styles.titleWrapper} ${
            selectedSearchMode === "resort" ? styles.selected : ""
          }`}
          onClick={() => setSelectedSearchMode("resort")}
        >
          <Image
            className={styles.imageTitle}
            src={
              selectedSearchMode === "resort" ? SearchSelected : SearchDefault
            }
            preview={false}
          />
          <H6 className={styles.title} weight={"medium"}>
            {t("home.searchbar.byresort")}
          </H6>
        </div>
      </div>
      <div
        className={styles.bodyContainer}
        style={{ padding: getBodyPadding(), width: stepsEnum[steps].bodyWidth }}
      >
        <Form
          variant={screens.lg ? "filled" : "outlined"}
          style={{ width: "100%" }}
          autoComplete={"off"}
          onFinish={stepsEnum[steps].handleOk}
          form={form}
          requiredMark={steps === step.FULL_SEARCH}
          layout={"vertical"}
          initialValues={{ adults: "0", kids: "0" }}
        >
          <Row
            style={{ width: "auto" }}
            gutter={screens.lg ? [22, 0] : [12, 12]}
          >
            {stepsEnum[steps].content}
            {stepsEnum[steps].btn}
            {stepsEnum[steps].showHint && (
              <P3
                className={styles.hint}
                style={{ transform: screens.lg ? "translateX(50%)" : "unset" }}
              >
                {t("home.filterBar.hint")}
                <span className={styles.cs}>
                  <a onClick={handleClickCS}>{t("normal.contactCS")}</a>
                </span>
              </P3>
            )}
          </Row>
        </Form>
      </div>
    </div>
  );
};

export default FilterBar;
